import * as React from 'react';

import { CreateArchiveModal } from '@/features/document-archives/core/components/modals/CreateArchiveModal';
import MoveIntoFolderModal from '@/features/document-archives/core/components/modals/MoveIntoFolderModal';
import { RenameArchiveItemModal } from '@/features/document-archives/core/components/modals/RenameArchiveItemModal';
import { ShareArchiveModal } from '@/features/document-archives/core/components/modals/ShareArchiveModal';
import { UploadArchiveFileModal } from '@/features/document-archives/core/components/modals/UploadArchiveFileModal';
import { EstateAnalysisSettingModal } from '@/features/estate-analytics/components/modals/estate-analysis-setting-modal';
import { UploadDocumentsModal } from '@/features/estate-document/components/upload-documents-modal';
import { Copy3DModelModal } from '@/features/estate-media/components/copy-3dmodel-modal';
import { UploadDigitalTwinsModal } from '@/features/estate-media/components/media-cards/upload-digital-twins-modal';
import { ShareDigiTwinModal } from '@/features/estate-media/components/modals/share-digi-twin-modal';
import { UpdateDigiTwinModal } from '@/features/estate-media/components/modals/update-digi-twin-modal';
import { UploadMediaModal } from '@/features/estate-media/components/upload-media-modal';
import { ViewMediaModal } from '@/features/estate-media/components/view-media-modal';
import { ChooseDestinationPanoramaModal } from '@/features/estate-media/components/virtual-tour/modals/choose-destination-panorama';
import { FloorPlanModal } from '@/features/estate-media/components/virtual-tour/modals/floor-plan-modal';
import { SphericalViewerModal } from '@/features/estate-media/components/virtual-tour/modals/preview-modal';
import { ShareVirtoulTourModal } from '@/features/estate-media/components/virtual-tour/modals/share-virtual-tour-modal';
import { SelectThumbnailModal } from '@/features/estates-property/components/select-thumbnail-modal';
import { ExportImageRenderingModal } from '@/features/image-rendering/components/export-image-rendering-modal';
import { MediaLibraryModal } from '@/features/image-rendering/components/media-library-modal';
import { MobileImageUploadModal } from '@/features/image-rendering/components/mobile-image-upload-modal';
import { AddContactModal } from '@/features/tenants/components/modals/add-contact-modal';
import { AddEmployeeModal } from '@/features/tenants/components/modals/add-employee-modal';
import { AddSubtenantModal } from '@/features/tenants/components/modals/add-subtenant-modal';
import { EditEmployeeRoleModal } from '@/features/tenants/components/modals/edit-employee-role-modal';

import { ConfirmDeleteModal } from './confirm-delete-modal';
import { ConfirmationModal } from './confirmation-modal';
import { CopyEstateModal } from './copy-estate-modal';
import { CopyProjectObjectModal } from './copy-project-object-modal';
import { CreateEstateModal } from './create-estate-modal';
import { CreateProjectObjectModal } from './create-project-object-modal';
import { EditEstateModal } from './edit-estate-modal';
import { EditProjectModal } from './edit-project-modal';
import { GlobalSearchModal } from './global-search-modal';
import { MapFullscreenModal } from './MapFullscreenModal';
import { EMITTER_EVENT, eventEmitter } from './modals.utils';
import type {
  ModalId,
  ModalProps,
  ModalRouteProps,
  ModalsBrowserProps,
  ModalsCallArgs,
} from './modals-root.types';
import { ShareShadowmapModal } from './share-shadowmap-modal';
import { SunlightSimulationModal } from './sunlight-simulation-modal';

const ModalsBrowser: React.FC<ModalsBrowserProps> = ({ id, children, modalProps }) => {
  return React.Children.map(children, (child) => {
    const { id: childId, Modal } = child.props;
    if (childId === id) {
      return <Modal key={id} {...modalProps} />;
    }
    return null;
  });
};

const ModalRoute: React.FC<ModalRouteProps> = () => {
  return null;
};

export const ModalsRoot: React.FC = () => {
  const [modalId, setModalId] = React.useState<ModalId | ''>('');
  const [modalProps, setModalProps] = React.useState<ModalProps>();

  const setCurrentModalHandle = React.useCallback((...args: ModalsCallArgs) => {
    const [id, modalProps] = args;
    if (modalProps) setModalProps(modalProps);
    return setModalId(id);
  }, []);

  React.useEffect(() => {
    eventEmitter.addListener(EMITTER_EVENT, setCurrentModalHandle);
    return () => {
      eventEmitter.removeListener(EMITTER_EVENT, setCurrentModalHandle);
    };
  }, [setCurrentModalHandle]);

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (!modalId) {
      document.body.style.overflow = 'auto';
    }
  }, [modalId]);

  if (!modalId) return null;

  return (
    <ModalsBrowser id={modalId} modalProps={modalProps as ModalProps}>
      <ModalRoute id="confirmation-modal" Modal={ConfirmationModal} />
      <ModalRoute id="edit-employee-role" Modal={EditEmployeeRoleModal} />
      <ModalRoute id="add-employee" Modal={AddEmployeeModal} />
      <ModalRoute id="add-contact" Modal={AddContactModal} />
      <ModalRoute id="edit-estate" Modal={EditEstateModal} />
      <ModalRoute id="create-estate" Modal={CreateEstateModal} />
      <ModalRoute id="export-image-rendering-modal" Modal={ExportImageRenderingModal} />
      <ModalRoute id="upload-documents" Modal={UploadDocumentsModal} />
      <ModalRoute id="upload-media" Modal={UploadMediaModal} />
      <ModalRoute id="upload-digital-twins" Modal={UploadDigitalTwinsModal} />
      <ModalRoute id="select-thumbnail" Modal={SelectThumbnailModal} />
      <ModalRoute id="spherical-image-preview" Modal={SphericalViewerModal} />
      <ModalRoute id="floor-plan" Modal={FloorPlanModal} />
      <ModalRoute id="share-virtual-tour" Modal={ShareVirtoulTourModal} />
      <ModalRoute id="choose-destination-panorama" Modal={ChooseDestinationPanoramaModal} />
      <ModalRoute id="global-search" Modal={GlobalSearchModal} />
      <ModalRoute id="view-media" Modal={ViewMediaModal} />
      <ModalRoute id="copy-3dmodel" Modal={Copy3DModelModal} />
      <ModalRoute id="update-digi-twin" Modal={UpdateDigiTwinModal} />
      <ModalRoute id="add-subtenant" Modal={AddSubtenantModal} />
      <ModalRoute id="media-library" Modal={MediaLibraryModal} />
      <ModalRoute id="mobile-image-upload" Modal={MobileImageUploadModal} />
      <ModalRoute id="share-digi-twin" Modal={ShareDigiTwinModal} />
      <ModalRoute id="create-project-object" Modal={CreateProjectObjectModal} />
      <ModalRoute id="copy-project-object" Modal={CopyProjectObjectModal} />
      <ModalRoute id="edit-project" Modal={EditProjectModal} />
      <ModalRoute id="confirm-delete-modal" Modal={ConfirmDeleteModal} />
      <ModalRoute id="estate-analysis-setting" Modal={EstateAnalysisSettingModal} />
      <ModalRoute id="map-fullscreen" Modal={MapFullscreenModal} />
      <ModalRoute id="sunlight-simulation" Modal={SunlightSimulationModal} />
      <ModalRoute id="share-shadowmap" Modal={ShareShadowmapModal} />
      <ModalRoute id="rename-archive-item" Modal={RenameArchiveItemModal} />
      <ModalRoute id="share-archive" Modal={ShareArchiveModal} />
      <ModalRoute id="create-archive" Modal={CreateArchiveModal} />
      <ModalRoute id="move-into-folder" Modal={MoveIntoFolderModal} />
      <ModalRoute id="upload-archive-file" Modal={UploadArchiveFileModal} />
      <ModalRoute id="copy-estate" Modal={CopyEstateModal} />
    </ModalsBrowser>
  );
};
